import React, { useEffect, useState } from "react";
import { Card, Row, Col, Statistic, Typography, Spin, Divider } from "antd";
import {
  TeamOutlined,
  ProjectOutlined,
  FileTextOutlined,
  ProfileOutlined,
  FileDoneOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import axios from "axios";
import TaskService from "../Services/Manage/TaskService";
import { ENDPOINT } from "../Services/ENDPOINT";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { EnquiryService } from "../Services/Manage/EnquiryService";
import { QuotationService } from "../Services/Manage/QuotationService";
import { InvoiceService } from "../Services/Manage/InvoiceService";
import { UserService } from "../Services/Manage/UserService";

const { Title } = Typography;

const DashboardPage = () => {
  const [employeeCount, setEmployeeCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [enquiryCount, setEnquiryCount] = useState(0);
  const [quotationCount, setQuotationCount] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const COLORS = ["#1890ff", "#52c41a", "#faad14"];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const usersRes = await axios.get(ENDPOINT.Users);
        setEmployeeCount(usersRes.data?.data?.length || 0);

        const user = await UserService.getOmeUser();
        const userId = user?.uid;

        const resp = await TaskService.getAssignedTasks(userId);
 
        const allProjects = resp?.data || [];

        const assignedTasks = allProjects.flatMap(project => project.assigned_tasks || []);

        setTaskCount(assignedTasks.length);

        setProjectCount(allProjects.length);


        const enquiryRes = await EnquiryService.getAllEnquiriesById(userId);
        setEnquiryCount(enquiryRes?.data?.length || 0);

        const quotationRes = await QuotationService.getAllQuotationByUID(userId);
        setQuotationCount(quotationRes?.data?.length || 0);

        const invoiceRes = await InvoiceService.getAllInvoiceById(userId);
        setInvoiceCount(invoiceRes?.data?.length || 0);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "100px" }}>
        <Spin size="large" />
      </div>
    );
  }

  const chartData = [
    { name: "Employees", value: employeeCount },
    { name: "Projects", value: projectCount },
    { name: "Tasks", value: taskCount },
  ];


  return (
    <div>
      <Card
        title={<Title level={4} style={{ margin: 0 }}>Dashboard</Title>}
        variant="borderless"
        style={{ backgroundColor: "#fff" }}
      >
        <Row gutter={24} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} md={8}>
            <Card variant="borderless" style={{ borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <Statistic title="Total Employees" value={employeeCount} prefix={<TeamOutlined style={{ color: "#1890ff" }} />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card variant="borderless" style={{ borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <Statistic title="Total Projects" value={projectCount} prefix={<ProjectOutlined style={{ color: "#52c41a" }} />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card variant="borderless" style={{ borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <Statistic title="Total Tasks" value={taskCount} prefix={<FileTextOutlined style={{ color: "#faad14" }} />} />
            </Card>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Card title="Operational Overview" variant="borderless" style={{ borderRadius: "12px", height: "100%" }}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Operational Analysis" variant="borderless" style={{ borderRadius: "12px", height: "100%" }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />

                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#1890ff" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

       <Divider
  orientation="center"
  style={{ margin: "32px 0", fontSize: "18px", fontWeight: 600 }}
>
  Sales and Marketing Overview
</Divider>


        <Row gutter={24} style={{ marginBottom: "24px" }}>
          <Col xs={24} sm={12} md={8}>
            <Card variant="borderless" style={{ borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <Statistic title="Total Enquiries" value={enquiryCount} prefix={<ProfileOutlined style={{ color: "#1890ff" }} />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card variant="borderless" style={{ borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <Statistic title="Total Quotations" value={quotationCount} prefix={<FileDoneOutlined style={{ color: "#52c41a" }} />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card variant="borderless" style={{ borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <Statistic title="Total Invoices" value={invoiceCount} prefix={<FileProtectOutlined style={{ color: "#fa541c" }} />} />
            </Card>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Card title="Business Overview" variant="borderless" style={{ borderRadius: "12px", height: "100%" }}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: "Enquiries", value: enquiryCount },
                      { name: "Quotations", value: quotationCount },
                      { name: "Invoices", value: invoiceCount },
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    <Cell fill="#1890ff" />
                    <Cell fill="#52c41a" />
                    <Cell fill="#fa541c" />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card title="Business Analysis" variant="borderless" style={{ borderRadius: "12px", height: "100%" }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[
                    { name: "Enquiries", value: enquiryCount },
                    { name: "Quotations", value: quotationCount },
                    { name: "Invoices", value: invoiceCount },
                  ]}
                  margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis  />

                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#1890ff" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default DashboardPage;
